import { logWarn } from "./logger";
const LEGACY_LOCAL_KEY = "finvault_local_v1";
const LEGACY_SECURE_KEY = "finvault_local_v2_secure";
const META_KEY = "finvault_local_v3_meta";
const IDB_NAME = "finvault_indexed_vault_v1";
const IDB_STORE = "kv";
const IDB_PLAIN_KEY = "plainVault";
const IDB_SECURE_KEY = "secureEnvelope";
const TEXT = new TextEncoder();
const DECODER = new TextDecoder();

let vaultKey = null;
let vaultCache = null;
let vaultUnlocked = false;
let idbPromise = null;

const hasWebCrypto = () => typeof crypto !== "undefined" && crypto.subtle && crypto.getRandomValues;
const hasIndexedDB = () => typeof indexedDB !== "undefined";

function bytesToBase64(bytes) {
  let bin = "";
  bytes.forEach(b => { bin += String.fromCharCode(b); });
  return btoa(bin);
}

function base64ToBytes(value = "") {
  const bin = atob(value);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) out[i] = bin.charCodeAt(i);
  return out;
}

function randomBase64(length = 16) {
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  return bytesToBase64(bytes);
}

function safeJsonParse(value, fallback = null) {
  try { return JSON.parse(value || ""); } catch { return fallback; }
}

function readMeta() {
  return safeJsonParse(localStorage.getItem(META_KEY), null) || {};
}

function writeMeta(patch = {}) {
  const meta = {
    v: 3,
    storage: hasIndexedDB() ? "indexedDB" : "localStorage",
    updatedAt: new Date().toISOString(),
    ...readMeta(),
    ...patch
  };
  try { localStorage.setItem(META_KEY, JSON.stringify(meta)); } catch {}
  return meta;
}

function estimateBytes(value) {
  try { return new Blob([JSON.stringify(value || {})]).size; } catch { return 0; }
}

function openVaultDb() {
  if (!hasIndexedDB()) return Promise.resolve(null);
  if (idbPromise) return idbPromise;
  idbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(IDB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(IDB_STORE)) db.createObjectStore(IDB_STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error || new Error("Could not open IndexedDB vault."));
  }).catch(err => {
    logWarn("FinVault IndexedDB unavailable, fallback storage used", err);
    return null;
  });
  return idbPromise;
}

async function idbGet(key) {
  const db = await openVaultDb();
  if (!db) return null;
  return new Promise((resolve, reject) => {
    const tx = db.transaction(IDB_STORE, "readonly");
    const req = tx.objectStore(IDB_STORE).get(key);
    req.onsuccess = () => resolve(req.result || null);
    req.onerror = () => reject(req.error || new Error("IndexedDB read failed."));
  });
}

async function idbSet(key, value) {
  const db = await openVaultDb();
  if (!db) return false;
  return new Promise((resolve, reject) => {
    const tx = db.transaction(IDB_STORE, "readwrite");
    tx.objectStore(IDB_STORE).put(value, key);
    tx.oncomplete = () => resolve(true);
    tx.onerror = () => reject(tx.error || new Error("IndexedDB write failed."));
  });
}

async function idbDelete(key) {
  const db = await openVaultDb();
  if (!db) return false;
  return new Promise((resolve, reject) => {
    const tx = db.transaction(IDB_STORE, "readwrite");
    tx.objectStore(IDB_STORE).delete(key);
    tx.oncomplete = () => resolve(true);
    tx.onerror = () => reject(tx.error || new Error("IndexedDB delete failed."));
  });
}

function readPlainLocal() {
  return safeJsonParse(localStorage.getItem(LEGACY_LOCAL_KEY), {}) || {};
}

function readSecureLocalEnvelope() {
  return safeJsonParse(localStorage.getItem(LEGACY_SECURE_KEY), null);
}

async function readPlainVault() {
  if (vaultCache) return vaultCache;
  const row = await idbGet(IDB_PLAIN_KEY);
  if (row?.data && typeof row.data === "object") {
    vaultCache = row.data;
    writeMeta({ encrypted: false, sizeBytes: estimateBytes(row.data), plainInIndexedDB: true });
    return vaultCache;
  }

  const legacy = readPlainLocal();
  if (legacy && Object.keys(legacy).length > 0) {
    vaultCache = legacy;
    await writePlainVault(legacy);
    try { localStorage.removeItem(LEGACY_LOCAL_KEY); } catch {}
    return vaultCache;
  }

  vaultCache = {};
  return vaultCache;
}

async function writePlainVault(data) {
  vaultCache = data || {};
  const wrote = await idbSet(IDB_PLAIN_KEY, { v: 1, data: vaultCache, updatedAt: new Date().toISOString() });
  if (wrote) {
    await idbDelete(IDB_SECURE_KEY);
    try { localStorage.removeItem(LEGACY_LOCAL_KEY); localStorage.removeItem(LEGACY_SECURE_KEY); } catch {}
    writeMeta({ encrypted: false, format: "plain-json", sizeBytes: estimateBytes(vaultCache), plainInIndexedDB: true });
    return;
  }
  writeMeta({ encrypted: false, storage: "indexedDB-unavailable", format: "plain-json", sizeBytes: estimateBytes(vaultCache), plainInIndexedDB: false });
  throw new Error("IndexedDB is unavailable. FinVault cannot safely persist local vault data on this device.");
}

async function readSecureEnvelope() {
  const row = await idbGet(IDB_SECURE_KEY);
  if (row?.envelope) return row.envelope;

  const legacy = readSecureLocalEnvelope();
  if (legacy?.salt && legacy?.data && legacy?.iv) {
    await idbSet(IDB_SECURE_KEY, { v: 1, envelope: legacy, migratedAt: new Date().toISOString() });
    try { localStorage.removeItem(LEGACY_SECURE_KEY); } catch {}
    writeMeta({ encrypted: true, format: "aes-gcm-v1", sizeBytes: estimateBytes(legacy), encryptedInIndexedDB: true });
    return legacy;
  }
  return null;
}

async function writeSecureEnvelope(envelope) {
  const wrote = await idbSet(IDB_SECURE_KEY, { v: 1, envelope, updatedAt: new Date().toISOString() });
  if (wrote) {
    await idbDelete(IDB_PLAIN_KEY);
    try { localStorage.removeItem(LEGACY_LOCAL_KEY); localStorage.removeItem(LEGACY_SECURE_KEY); } catch {}
    writeMeta({ encrypted: true, format: "aes-gcm-v1", sizeBytes: estimateBytes(envelope), encryptedInIndexedDB: true, plainInIndexedDB: false });
    return;
  }
  writeMeta({ encrypted: true, storage: "indexedDB-unavailable", format: "aes-gcm-v1", sizeBytes: estimateBytes(envelope), encryptedInIndexedDB: false });
  throw new Error("IndexedDB is unavailable. FinVault cannot safely persist encrypted local vault data on this device.");
}

async function deriveAesKey(pin, salt, iterations = 210000) {
  if (!hasWebCrypto()) throw new Error("Secure vault encryption needs Web Crypto support.");
  const keyMaterial = await crypto.subtle.importKey(
    "raw",
    TEXT.encode(String(pin || "")),
    "PBKDF2",
    false,
    ["deriveKey"]
  );
  return crypto.subtle.deriveKey(
    { name: "PBKDF2", salt: base64ToBytes(salt), iterations, hash: "SHA-256" },
    keyMaterial,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"]
  );
}

async function encryptWithKey(key, data) {
  const iv = new Uint8Array(12);
  crypto.getRandomValues(iv);
  const cipher = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    key,
    TEXT.encode(JSON.stringify(data || {}))
  );
  return { iv: bytesToBase64(iv), data: bytesToBase64(new Uint8Array(cipher)) };
}

async function decryptWithKey(key, envelope) {
  const plain = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: base64ToBytes(envelope.iv) },
    key,
    base64ToBytes(envelope.data)
  );
  return JSON.parse(DECODER.decode(plain) || "{}");
}

export function isLocalVaultEncrypted() {
  const meta = readMeta();
  return Boolean(meta.encrypted || localStorage.getItem(LEGACY_SECURE_KEY));
}

export function isLocalVaultUnlocked() {
  return !isLocalVaultEncrypted() || vaultUnlocked;
}

export function getLocalVaultSecurityStatus() {
  const meta = readMeta();
  return {
    encrypted: isLocalVaultEncrypted(),
    unlocked: isLocalVaultUnlocked(),
    hasPlainCopy: Boolean(localStorage.getItem(LEGACY_LOCAL_KEY)),
    provider: hasWebCrypto() ? "AES-GCM + PBKDF2" : "unavailable",
    storage: meta.storage || (hasIndexedDB() ? "indexedDB" : "localStorage"),
    indexedDB: Boolean(meta.plainInIndexedDB || meta.encryptedInIndexedDB),
    sizeBytes: Number(meta.sizeBytes || 0)
  };
}

export function getLocalVaultDataSync() {
  if (vaultCache) return vaultCache;
  // Legacy localStorage vault data is migrated by async loaders only.
  // Returning an empty snapshot here avoids exposing stale plain data through sync callers.
  return {};
}

export async function getLocalVaultData() {
  if (isLocalVaultEncrypted()) return vaultCache || {};
  return readPlainVault();
}

export function getLocalVaultApproxBytes() {
  const meta = readMeta();
  if (meta.sizeBytes) return Number(meta.sizeBytes || 0);
  try {
    const encrypted = localStorage.getItem(LEGACY_SECURE_KEY) || "";
    const plain = localStorage.getItem(LEGACY_LOCAL_KEY) || "";
    return new Blob([encrypted || plain]).size;
  } catch { return 0; }
}

export async function persistLocalVaultData(data) {
  vaultCache = data || {};
  if (isLocalVaultEncrypted()) {
    if (!vaultKey) throw new Error("Local vault is locked. Unlock with PIN before saving.");
    const envelope = await readSecureEnvelope();
    const salt = envelope?.salt || randomBase64(16);
    const iterations = envelope?.iterations || 210000;
    const payload = await encryptWithKey(vaultKey, vaultCache);
    await writeSecureEnvelope({
      v: 1,
      alg: "AES-GCM",
      kdf: "PBKDF2-SHA256",
      iterations,
      salt,
      updatedAt: new Date().toISOString(),
      ...payload
    });
    return;
  }
  await writePlainVault(vaultCache);
}

export async function migrateLocalVaultToIndexedDB() {
  if (isLocalVaultEncrypted()) {
    const legacy = readSecureLocalEnvelope();
    if (legacy?.salt && legacy?.data && legacy?.iv) {
      await writeSecureEnvelope(legacy);
      return true;
    }
    const existing = await readSecureEnvelope();
    return Boolean(existing);
  }
  const data = await readPlainVault();
  await writePlainVault(data || {});
  return true;
}

export async function enableLocalVaultEncryption(pin) {
  if (!pin || String(pin).length < 4) return false;
  if (!hasWebCrypto()) return false;
  const existing = await readSecureEnvelope();
  const data = vaultCache || await getLocalVaultData();
  // If vault is already encrypted and currently not unlocked, try unlocking with the provided PIN.
  if (existing?.salt && existing?.data && existing?.iv && !vaultCache) return unlockLocalVault(pin);
  const salt = randomBase64(16);
  const iterations = 210000;
  const key = await deriveAesKey(pin, salt, iterations);
  const payload = await encryptWithKey(key, data);
  await writeSecureEnvelope({
    v: 1,
    alg: "AES-GCM",
    kdf: "PBKDF2-SHA256",
    iterations,
    salt,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    ...payload
  });
  vaultKey = key;
  vaultCache = data;
  vaultUnlocked = true;
  return true;
}

export async function unlockLocalVault(pin) {
  const envelope = await readSecureEnvelope();
  if (!envelope?.salt || !envelope?.data || !envelope?.iv) {
    vaultCache = await readPlainVault();
    vaultUnlocked = true;
    return true;
  }
  const key = await deriveAesKey(pin, envelope.salt, envelope.iterations || 210000);
  const data = await decryptWithKey(key, envelope);
  vaultKey = key;
  vaultCache = data || {};
  vaultUnlocked = true;
  return true;
}

export async function disableLocalVaultEncryption() {
  const data = vaultCache || await getLocalVaultData();
  await writePlainVault(data || {});
  await idbDelete(IDB_SECURE_KEY);
  try { localStorage.removeItem(LEGACY_SECURE_KEY); } catch {}
  vaultKey = null;
  vaultUnlocked = false;
}

export function clearLocalVaultSession() {
  vaultKey = null;
  vaultUnlocked = false;
  vaultCache = null;
}
